import type { ColumnDef } from '@tanstack/react-table';
import type { User } from '@/api/users/types';
import { Badge } from '@/components/ui/badge';
import { UserAvatar } from '@/components/user-avatar';

const formatMetaValue = (value: unknown) => {
  if (typeof value === 'string') {
    return value || '""';
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return value === undefined ? undefined : JSON.stringify(value);
};

export const getColumns = (metaKey?: string): ColumnDef<User>[] => [
  {
    id: 'avatar',
    accessorKey: 'publicId',
    header: '',
    cell: ({ getValue }) => {
      const userId = getValue() as string | undefined;
      return <UserAvatar userId={userId} />;
    },
  },
  {
    accessorKey: 'createdAt',
    header: 'Created',
  },
  {
    accessorKey: 'email',
    header: 'Email',
  },
  ...(metaKey
    ? [
        {
          id: 'matchedMeta',
          header: `Metadata: ${metaKey}`,
          cell: ({ row }) => {
            const value = row.original.userMeta?.[metaKey];
            const formattedValue = formatMetaValue(value);

            return formattedValue ? (
              <span className="block max-w-48 truncate font-mono text-xs" title={formattedValue}>
                {formattedValue}
              </span>
            ) : (
              <span className="text-muted-foreground">—</span>
            );
          },
        } satisfies ColumnDef<User>,
      ]
    : []),
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ getValue }) => {
      const status = getValue() as string | undefined;
      return (
        <Badge
          className="w-17 justify-center"
          variant={status === 'active' ? 'default' : 'outline'}
        >
          {status}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'commentsCount',
    header: 'Comments',
    meta: {
      className: 'text-end',
    },
  },
  {
    accessorKey: 'voteCount',
    header: 'Votes',
    meta: {
      className: 'text-end',
    },
  },
  {
    accessorKey: 'requestCount',
    header: 'Requests',
    meta: {
      className: 'text-right',
    },
  },
];
